/**
 * @module  public/js/core/crmservice.js
 * @class   CRMService
 * @summary CRM abstraction that hides HubSpot and Salesforce wire-format quirks.
 *
 * @description
 * Builds object-level REST endpoints, converts dates and maps property names so
 * stores/controllers remain CRM-agnostic. No network calls occur here; ApiClient
 * handles HTTP while CRMService focuses purely on formatting logic.
 *
 * ## Key Features
 * – Build fully-qualified object endpoints for HubSpot or Salesforce.
 * – Convert dates between ISO-8601 and CRM-specific formats.
 * – Map generic property bags to CRM field names.
 * – Extract paging cursors/links from CRM responses.
 *
 * ## Public API
 * – `buildObjectEndpoint(type, verb, params, crm)` — construct REST URL.
 * – `isoToCrmDate(isoDate, crm)` — convert ISO date to CRM format.
 * – `crmDateToIso(crmDate, crm)` — normalise CRM date to ISO string.
 * – `mapPropsToCrm(props, crm)` — rename generic keys to CRM equivalents.
 * – `getNextPagePointer(responseBody, crm)` — return paging cursor or null.
 *
 * ## Dependencies
 * (none)
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

export default class CRMService {
  /**
   * @param {{ endpointService?: { [k:string]: Function } }} deps
   */
  constructor({ endpointService, crmResolver } = {}) {
    this.endpointService = endpointService;
    this.crmResolver = crmResolver;
  }

  /**
   * Resolve the active CRM provider for a user (delegates to crmUtils).
   * @param {string} userId
   * @returns {Promise<'hubspot'|'salesforce'|'local'|null>}
   */
  async getCurrentCRM(userId) {
    if (typeof this.crmResolver === 'function') {
      return this.crmResolver(userId);
    }

    const mod = await import('../services/crmUtils.js');
    return mod.getCurrentCRM(userId);
  }

  /**
   * Build a CRM‑specific REST endpoint for a given object / action.
   *
   * @param {'accounts'|'contacts'|'deals'|'tasks'|'meetings'|'products'} objectType
   * @param {'GET'|'POST'|'PATCH'|'DELETE'} httpVerb
   * @param {Record<string, unknown>} [params] – id, paging, filters …
   * @param {'hubspot'|'salesforce'} crm
   * @returns {string} Fully‑qualified URL (without query string)
   */
  buildObjectEndpoint(objectType, httpVerb, params = {}, crm) {
    const { endpointService } = this;
    if (!endpointService) return '';

    const { id, accountId, userId } = params;
    const safeCrm = crm || params.crm;

    const byType = {
      contacts: () => {
        if (httpVerb === 'GET' && id) {
          return endpointService.getContactDetails({
            userId,
            contactId: id,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'GET' && accountId) {
          return endpointService.getAccountContacts({
            userId,
            accountId,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'PATCH' && id) {
          return endpointService.updateContactDetails({
            userId,
            contactId: id,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'POST') {
          return endpointService.createContact({ userId, crm: safeCrm });
        }
        return '';
      },
      accounts: () => {
        if (httpVerb === 'GET' && id) {
          return endpointService.getAccountDetails({
            userId,
            accountId: id,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'GET') {
          return endpointService.getAccountsByOwner({ userId, crm: safeCrm });
        }
        if (httpVerb === 'PATCH' && id) {
          return endpointService.updateAccountDetails({
            userId,
            accountId: id,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'POST') {
          return endpointService.createCompany({ userId, crm: safeCrm });
        }
        return '';
      },
      deals: () => {
        if (httpVerb === 'GET' && id) {
          return endpointService.getDealDetails({
            userId,
            dealId: id,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'POST') {
          return endpointService.createDeal({ userId, crm: safeCrm });
        }
        if (httpVerb === 'PATCH' && id) {
          return endpointService.updateDealDetails({
            userId,
            dealId: id,
            crm: safeCrm,
          });
        }
        return '';
      },
      tasks: () => {
        return endpointService.manageUserTask({ userId, crm: safeCrm });
      },
      meetings: () => {
        if (httpVerb === 'GET') {
          return endpointService.getMeetingEngagements({
            userId,
            crm: safeCrm,
          });
        }
        if (httpVerb === 'POST') {
          return endpointService.saveMeetingEngagement({
            userId,
            crm: safeCrm,
          });
        }
        return '';
      },
      products: () => {
        if (httpVerb === 'GET') {
          return endpointService.getProducts({ userId, crm: safeCrm });
        }
        return '';
      },
    };

    return byType[objectType]?.() || '';
  }

  /**
   * Convert an ISO‑8601 date to the CRM’s expected format.
   *
   * @param {string} isoDate – e.g. "2025-03-18"
   * @param {'hubspot'|'salesforce'} crm
   * @returns {string}
   */
  isoToCrmDate(isoDate, crm) {
    if (!isoDate) return '';
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return '';

    if (crm === 'hubspot') {
      parsed.setHours(0, 0, 0, 0);
      return parsed.getTime();
    }

    // Salesforce expects YYYY-MM-DD for date fields
    return parsed.toISOString().slice(0, 10);
  }

  /**
   * Convert a CRM date value back to ISO‑8601 (UTC, YYYY‑MM‑DD).
   *
   * @param {string|number} crmDate
   * @param {'hubspot'|'salesforce'} crm
   * @returns {string}
   */
  crmDateToIso(crmDate, crm) {
    if (crmDate === null || crmDate === undefined) return '';
    if (crm === 'hubspot') {
      const millis = typeof crmDate === 'number' ? crmDate : Number(crmDate);
      if (!Number.isFinite(millis)) return '';
      const d = new Date(millis);
      return d.toISOString().slice(0, 10);
    }

    // Salesforce can return either YYYY-MM-DD or full ISO timestamps
    if (typeof crmDate === 'string' && crmDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return crmDate;
    }

    const parsed = new Date(crmDate);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  }

  /**
   * Map a generic property bag to CRM‑specific field names.
   *
   * @param {Record<string, unknown>} genericProps
   * @param {'hubspot'|'salesforce'} crm
   * @returns {Record<string, unknown>}
   */
  mapPropsToCrm(genericProps, crm) {
    const hubspotMap = {
      firstName: 'firstname',
      lastName: 'lastname',
      email: 'email',
      phone: 'phone',
      mobilePhone: 'mobilephone',
      jobTitle: 'jobtitle',
      company: 'company',
      companyId: 'associatedcompanyid',
      amount: 'amount',
      closeDate: 'closedate',
      stage: 'dealstage',
      pipeline: 'pipeline',
      subject: 'hs_task_subject',
      status: 'hs_task_status',
      dueDate: 'hs_timestamp',
      description: 'hs_task_body',
      associatedCompanyId: 'associatedcompanyid',
      associatedContactId: 'associations.contact',
    };

    const salesforceMap = {
      firstName: 'FirstName',
      lastName: 'LastName',
      email: 'Email',
      phone: 'Phone',
      mobilePhone: 'MobilePhone',
      jobTitle: 'Title',
      company: 'AccountName',
      companyId: 'AccountId',
      website: 'Website',
      domain: 'Website',
      city: 'City',
      state: 'State',
      postalCode: 'PostalCode',
      country: 'Country',
      amount: 'Amount',
      closeDate: 'CloseDate',
      stage: 'StageName',
      pipeline: 'PipelineId',
      subject: 'Subject',
      status: 'Status',
      dueDate: 'ActivityDate',
      description: 'Description',
      associatedCompanyId: 'WhatId',
      associatedContactId: 'WhoId',
    };

    const map = crm === 'salesforce' ? salesforceMap : hubspotMap;
    return Object.entries(genericProps || {}).reduce((acc, [key, val]) => {
      const mappedKey = map[key] || key;
      acc[mappedKey] = val;
      return acc;
    }, {});
  }

  /**
   * Extract the next‑page cursor / link from the CRM response.
   *
   * @param {Record<string, unknown>} responseBody – Already JSON‑parsed
   * @param {'hubspot'|'salesforce'} crm
   * @returns {string|null} – URL or cursor token, null if no more pages
   */
  getNextPagePointer(responseBody, crm) {
    if (!responseBody || typeof responseBody !== 'object') return null;
    if (crm === 'hubspot') {
      return responseBody?.paging?.next?.after || null;
    }

    // Salesforce exposes either a `nextRecordsUrl` or pagination token
    return responseBody?.nextRecordsUrl || responseBody?.nextPageToken || null;
  }
}
